import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import NotificationApi from "api/NotificationApi";
import { authActions, expireSession } from "../../context/AuthContext";
import { getApiData, getApiErrorCode, type ApiError } from "../../types/api";
import type { NotificationItem } from "../../types/state";

const EMPTY_NOTIFICATIONS: NotificationItem[] = [];

function isUnread(notification: NotificationItem): boolean {
  return notification.read !== true && notification.read !== 1 && notification.read !== "1";
}

function handleSessionError(error: ApiError) {
  if (getApiErrorCode(error) !== "NOT_LOGGED") {
    return;
  }

  expireSession();
}

export function notificationsQueryKey(customerId?: number | string | null) {
  return ["notifications", customerId || "anonymous"];
}

function setUnreadCount(
  customerId: number | string | null | undefined,
  notifications: NotificationItem[]
) {
  if (!customerId) {
    return;
  }

  authActions.setUnreadNotificationCount(customerId, notifications.filter(isUnread).length);
}

export function useNotifications(customerId?: number | string | null) {
  const query = useQuery({
    queryKey: notificationsQueryKey(customerId),
    enabled: !!customerId,
    queryFn: () =>
      NotificationApi.getAll()
        .then((res) => getApiData(res, []) as NotificationItem[])
        .catch((error: ApiError) => {
          handleSessionError(error);
          return [];
        }),
  });

  const notifications = query.data ?? EMPTY_NOTIFICATIONS;
  useEffect(() => {
    if (query.isSuccess) {
      setUnreadCount(customerId, notifications);
    }
  }, [customerId, notifications, query.isSuccess]);

  return {
    ...query,
    notifications,
    notificationsLoaded: query.isSuccess,
  };
}

export function useMarkNotificationAsRead(customerId?: number | string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: number | string) =>
      NotificationApi.update({ id: notificationId, read: 1 }).then(() => notificationId),
    onSuccess: (notificationId) => {
      const key = notificationsQueryKey(customerId);
      const current = queryClient.getQueryData<NotificationItem[]>(key) || [];
      const next = current.map((notification) =>
        String(notification.id) === String(notificationId)
          ? { ...notification, read: true }
          : notification
      );
      queryClient.setQueryData(key, next);
      setUnreadCount(customerId, next);
    },
    onError: (error: ApiError) => handleSessionError(error),
  });
}

export function useDeleteNotification(customerId?: number | string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: number | string) =>
      NotificationApi.delete(notificationId).then(() => notificationId),
    onSuccess: (notificationId) => {
      const key = notificationsQueryKey(customerId);
      const current = queryClient.getQueryData<NotificationItem[]>(key) || [];
      const next = current.filter(
        (notification) => String(notification.id) !== String(notificationId)
      );
      queryClient.setQueryData(key, next);
      setUnreadCount(customerId, next);
    },
    onError: (error: ApiError) => handleSessionError(error),
  });
}

export function useMarkNotificationsAsRead(customerId?: number | string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationIds: Array<number | string>) =>
      NotificationApi.markManyAsRead(notificationIds).then(() => notificationIds),
    onSuccess: (updatedIds) => {
      const ids = updatedIds.map((id) => String(id));
      const key = notificationsQueryKey(customerId);
      const current = queryClient.getQueryData<NotificationItem[]>(key) || [];
      const next = current.map((notification) =>
        ids.includes(String(notification.id)) ? { ...notification, read: true } : notification
      );
      queryClient.setQueryData(key, next);
      setUnreadCount(customerId, next);
    },
    onError: (error: ApiError) => handleSessionError(error),
  });
}

export function useDeleteNotifications(customerId?: number | string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationIds: Array<number | string>) =>
      NotificationApi.deleteMany(notificationIds).then(() => notificationIds),
    onSuccess: (deletedIds) => {
      const ids = deletedIds.map((id) => String(id));
      const key = notificationsQueryKey(customerId);
      const current = queryClient.getQueryData<NotificationItem[]>(key) || [];
      const next = current.filter((notification) => !ids.includes(String(notification.id)));
      queryClient.setQueryData(key, next);
      setUnreadCount(customerId, next);
    },
    onError: (error: ApiError) => handleSessionError(error),
  });
}

export function useMarkAllNotificationsAsRead(customerId?: number | string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => NotificationApi.updateAll(),
    onSuccess: () => {
      const key = notificationsQueryKey(customerId);
      const current = queryClient.getQueryData<NotificationItem[]>(key) || [];
      const next = current.map((notification) => ({ ...notification, read: true }));
      queryClient.setQueryData(key, next);
      setUnreadCount(customerId, next);
    },
    onError: (error: ApiError) => handleSessionError(error),
  });
}
